/* eslint-disable max-lines */
import React, { useMemo } from 'react';
import { Flex, Box, Text } from 'rebass/styled-components';
import { useDispatch } from 'react-redux';
import { Message, Panel, ProfilePicture } from '@getro/rombo';
import Link from 'next/link';
import PropTypes from 'prop-types';
import { useTheme } from 'styled-components';
import PageLayout from '../../components/layouts/Main/PageLayout';

import { applyToJobClicked } from '../../redux/actions/jobs';
import { NetworkSchema } from '../../schema/network';
import { useJobsLabel } from './hooks/useJobsLabel';
import { OnBoardApplication } from './OnBoardApplication';
import { DescriptionRenderer } from './components/DescriptionRenderer';
import { useRestJobApplication } from './hooks/useRestJobApplication';
import ApplyButton from './applyButton';

const Job = ({ network, currentJob }) => {
  const { colors } = useTheme();
  const dispatch = useDispatch();
  const { title, organization = {}, description, compensationPublic, postedAt, applicationMethod, source } = currentJob;
  const { name: organizationName, logoUrl, slug } = organization || {};

  const { compensationLabel, jobFunctionsLabel, employmentTypesLabel, locationsLabel, applicationUrl } = useJobsLabel(
    currentJob,
    network,
  );
  useRestJobApplication();

  const onApplyClick = (origin = 'job_bottom') => {
    dispatch(applyToJobClicked({ network, job: currentJob, origin }));
  };

  const isActive = useMemo(() => !['closed', 'expired', 'deactivated'].includes(currentJob?.status), [currentJob]);

  const inActiveMessage = !isActive && currentJob && (
    <Message
      type="warning"
      content={
        <Flex sx={{ flexDirection: 'column', gap: '8px' }}>
          <Text as="P" fontWeight="600" fontSize="14px" color="text.dark">
            This job is no longer accepting applications
          </Text>
          <Link passHref href={`/companies/${slug}#content`} legacyBehavior>
            <Box variant="inactiveJobMessage" as="a">
              See open jobs at {organizationName}.
            </Box>
          </Link>
          <Link passHref href={`/jobs?q=${currentJob.title}`} legacyBehavior>
            <Box variant="inactiveJobMessage" as="a">
              See open jobs similar to &quot;{currentJob.title}&quot; {network.name}.
            </Box>
          </Link>
        </Flex>
      }
    />
  );

  return (
    <Panel px={['16px', '32px']} mt={5} maxWidth={800} width="100%" mx="auto" flexDirection="column">
      <Flex
        justifyContent="space-between"
        mb={isActive ? '40px' : '24px'}
        flexDirection={['column', 'row', 'row']}
        width="100%"
      >
        <Flex flexDirection="column" sx={{ gap: '24px' }} width="100%">
          <Box width="100%">
            <Text fontSize="28px" marginBottom={2} as="h2" color="text.dark">
              {title}
            </Text>
            <Link href={`/companies/${slug}#content`} legacyBehavior>
              <Flex alignItems="center" color="text" sx={{ columnGap: 2, cursor: 'pointer' }}>
                <Box width={32}>
                  <ProfilePicture variant="square" imageUrl={logoUrl} name={organizationName} />
                </Box>
                <Text as="p">{organizationName}</Text>
              </Flex>
            </Link>
          </Box>
          {inActiveMessage && <Box mb="16px">{inActiveMessage}</Box>}
          <Flex sx={{ columnGap: '8px' }} justifyContent="space-between">
            <Flex flexDirection="column" sx={{ rowGap: 2 }}>
              <Text a="p">
                {jobFunctionsLabel} {jobFunctionsLabel && employmentTypesLabel ? '·' : ''} {employmentTypesLabel}
              </Text>
              {locationsLabel && <Text a="p">{locationsLabel}</Text>}
              {compensationPublic && compensationLabel && <Text a="p">{compensationLabel}</Text>}
              <Text fontSize="14px" color="text.subtle">
                Posted{' '}
                {new Date(postedAt).getTime() < new Date().getTime() - 180 * 24 * 60 * 60 * 1000
                  ? '6+ months ago'
                  : `on ${new Date(postedAt).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                    })}`}
              </Text>
            </Flex>
          </Flex>
        </Flex>

        {applicationMethod !== 'on_job_board' && isActive && (
          <Box minWidth="132px" mt={['24px', '0']}>
            <ApplyButton onApplyClick={() => onApplyClick('job_top')} applicationUrl={applicationUrl} network={network}>
              Apply now
            </ApplyButton>
          </Box>
        )}
      </Flex>

      <DescriptionRenderer
        sx={{
          a: {
            color: `${colors.text.main} !important`,
          },
        }}
        organizationName={organizationName}
        description={description}
        source={source}
      />
      {isActive && applicationMethod === 'on_job_board' && <OnBoardApplication network={network} />}
      {isActive && applicationMethod !== 'on_job_board' && (
        <Flex
          sx={{
            justifyContent: 'center',
            flexDirection: 'column',
            mt: '40px',
            alignItems: 'center',
          }}
        >
          <ApplyButton
            network={network}
            onApplyClick={() => onApplyClick('job_bottom')}
            applicationUrl={applicationUrl}
            width={['100%', '136px']}
            height="40px"
            data-testid="button-apply-now"
          >
            Apply now
          </ApplyButton>
        </Flex>
      )}
      {!isActive && (
        <Box pb="40px" mt="40px">
          {inActiveMessage}
        </Box>
      )}
      <Flex
        mt="40px"
        alignItems="center"
        justifyContent={source === 'career_page' ? 'space-between' : 'center'}
        flexDirection={['column', 'row']}
        sx={{ gap: '40px' }}
      >
        <Link passHref href={`/companies/${slug}#content`} legacyBehavior>
          <Text
            as="a"
            textAlign="center"
            sx={{ textDecorationLine: 'underline', cursor: 'pointer', display: 'block' }}
            color={`${colors.text.main} !important`}
            fontSize="14px"
            lineHeight="22px"
          >
            See more open positions at {organizationName}
          </Text>
        </Link>
      </Flex>
    </Panel>
  );
};

Job.propTypes = {
  network: NetworkSchema.isRequired,
  currentJob: PropTypes.object.isRequired,
};

Job.Layout = PageLayout;

export default Job;
